import React, { useMemo } from 'react';
import * as Types from 'pages/Features/tabs/Profile/TimezoneAndWorkHours/typings';

import makeStyles from '@material-ui/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import FormGroup from '@material-ui/core/FormGroup';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';

import WeekDayCheckBox from './WeekDayCheckBox';
import WorkHoursSelector from './WorkHoursSelector';
import LocationSelector from './LocationSelector';

import {
  WEEK_DAY_NAMES,
  DEFAULT_WORK_HOURS,
} from 'pages/Features/tabs/Profile/TimezoneAndWorkHours/constants';

interface Props {
  value: Types.TimezoneAndWorkHoursFormState;
  errors: any;
  onChange: (key: string, value: any) => void;
}

function WorkHoursForm({ value, errors, onChange }: Props) {
  const classes = useStyles();
  const {
    workScheduleEnabled,
    hoursEnabled,
    locationEnabled,
    defaultWorkHoursEnabled,
    workHoursByDay,
  } = value;
  const workHoursByDayEntries = Object.entries(workHoursByDay) as Types.WorkHoursByDayEntries;
  const workingDayAndHours = useMemo(() => workHoursByDayEntries.filter(([_, value]) => !!value), [
    workHoursByDayEntries,
  ]) as Types.WorkingDayWorkHours[];

  const hasWorkingDay = !!workingDayAndHours.length;
  const hasMoreThanOneWorkingDay = workingDayAndHours.length > 1;
  const firstWorkingDay = hasWorkingDay && workingDayAndHours[0][0];
  const firstWorkingDayHours = hasWorkingDay ? workingDayAndHours[0][1] : DEFAULT_WORK_HOURS;

  const handleChangeHoursEnabled = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange('client.settings.timezoneAndWorkHours.hoursEnabled', e.target.checked);
    if (!e.target.checked && !locationEnabled) {
      onChange('client.settings.timezoneAndWorkHours.workScheduleEnabled', false);
      onChange('client.settings.timezoneAndWorkHours.defaultWorkHoursEnabled', false);
    }
  };

  const handleChangeLocationEnabled = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange('client.settings.timezoneAndWorkHours.locationEnabled', e.target.checked);
    if (!e.target.checked && !hoursEnabled) {
      onChange('client.settings.timezoneAndWorkHours.workScheduleEnabled', false);
      onChange('client.settings.timezoneAndWorkHours.defaultWorkHoursEnabled', false);
    }
  };

  const handleChangeDefaultWorkHoursEnabled = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange('client.settings.timezoneAndWorkHours.defaultWorkHoursEnabled', e.target.checked);
  };

  const handleChangeWorkHourOfDay = (
    day: Types.WeekDay,
    marker: 'startTime' | 'endTime',
    timeObject: any,
  ) => {
    onChange(`client.settings.timezoneAndWorkHours.workHoursByDay.${day}.${marker}`, timeObject);
  };

  const handleChangeLocationOfDay = (
    day: Types.WeekDay,
    newLocation: Types.WorkLocation | null,
  ) => {
    onChange(`client.settings.timezoneAndWorkHours.workHoursByDay.${day}.location`, newLocation);
  };

  const handleChangeWorkingOnDay = (day: Types.WeekDay, e: React.ChangeEvent<HTMLInputElement>) => {
    const newValueForDay = e.target.checked
      ? {
          day: WEEK_DAY_NAMES.indexOf(day),
          startTime: firstWorkingDayHours.startTime,
          endTime: firstWorkingDayHours.endTime,
          location: firstWorkingDayHours.location,
        }
      : null;

    onChange(`client.settings.timezoneAndWorkHours.workHoursByDay.${day}`, newValueForDay);
  };

  const handleApplyWorkHoursToAllDays = () => {
    if (workingDayAndHours.length < 2) {
      return;
    }
    const startTime = firstWorkingDayHours.startTime;
    const endTime = firstWorkingDayHours.endTime;
    const location = firstWorkingDayHours.location;

    const newWorkHoursByDay = workHoursByDayEntries.reduce((acc, entry) => {
      const day = entry[0] as Types.WeekDay;
      const value = entry[1];
      const dayNumberValue = WEEK_DAY_NAMES.indexOf(day);
      acc[day] = !!value ? { day: dayNumberValue, startTime, endTime, location } : null;
      return acc;
    }, {} as Types.WorkHoursByDay);

    onChange('client.settings.timezoneAndWorkHours.workHoursByDay', newWorkHoursByDay);
  };

  return (
    <div className={classes.root}>
      <div className={classes.toggles}>
        <FormControlLabel
          control={<Checkbox checked={hoursEnabled} onChange={handleChangeHoursEnabled} />}
          label="Enable Work Hours"
          className={classes.checkbox}
          disabled={!workScheduleEnabled}
        />
        <Typography variant="subtitle2" className={classes.marginLeft}>
          Allow users to display their daily working hours on their profile.
        </Typography>
        <FormControlLabel
          control={<Checkbox checked={locationEnabled} onChange={handleChangeLocationEnabled} />}
          label="Enable Work Location"
          className={classes.checkbox}
          disabled={!workScheduleEnabled}
        />
        <Typography variant="subtitle2" className={classes.marginLeft}>
          Allow users to display their normal daily work location on their profile.
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={defaultWorkHoursEnabled}
              onChange={handleChangeDefaultWorkHoursEnabled}
            />
          }
          label="Enable default Work Schedule"
          className={classes.checkbox}
          disabled={!workScheduleEnabled}
        />
        <Typography variant="subtitle2" className={classes.marginLeft}>
          If a default work schedule is set, people who edit their work schedule will start from the
          default setting.
        </Typography>
      </div>
      {defaultWorkHoursEnabled && (
        <>
          <FormGroup row>
            {WEEK_DAY_NAMES.map((day, i) => {
              return (
                <WeekDayCheckBox
                  key={`${day}-${i}`}
                  day={day}
                  checked={!!workHoursByDay[day]}
                  onChange={e => handleChangeWorkingOnDay(day, e)}
                  disabled={!defaultWorkHoursEnabled}
                />
              );
            })}
          </FormGroup>
          <div className={classes.workHourSelectors}>
            <div>
              {hasWorkingDay &&
                workingDayAndHours.map(([day, { startTime, endTime, location }], i) => {
                  const error = !!errors && errors[day];

                  return (
                    <div className={classes.row} key={`${i}-${day}`}>
                      <div className={classes.dayRowContent}>
                        <Typography className={classes.day}>{day}</Typography>
                        <div className={classes.hoursAndLocationAndErrors}>
                          <div className={classes.hoursAndLocation}>
                            {hoursEnabled && (
                              <WorkHoursSelector
                                clearable={hoursEnabled && locationEnabled}
                                error={!!errors && errors[day]}
                                disabled={!workScheduleEnabled || !hoursEnabled}
                                startTime={startTime}
                                endTime={endTime}
                                onChangeStartTime={e =>
                                  handleChangeWorkHourOfDay(day, 'startTime', e)
                                }
                                onChangeEndTime={e => handleChangeWorkHourOfDay(day, 'endTime', e)}
                              />
                            )}
                            {locationEnabled && (
                              <LocationSelector
                                day={day}
                                disabled={!locationEnabled || !workScheduleEnabled}
                                location={location || null}
                                onChange={newLocation =>
                                  handleChangeLocationOfDay(day, newLocation)
                                }
                              />
                            )}
                          </div>
                          {error && (
                            <Typography
                              className={classes.errorMessage}
                              variant="caption"
                              color="error"
                            >
                              {error}
                            </Typography>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
            {hasMoreThanOneWorkingDay && (
              <Tooltip title="Copy Schedule To All Work Days">
                <span>
                  <IconButton
                    onClick={handleApplyWorkHoursToAllDays}
                    aria-label="Copy Schedule To All Work Days"
                    className={classes.copyButton}
                    size="small"
                    disabled={
                      !defaultWorkHoursEnabled ||
                      (!!firstWorkingDay && !!errors && !!errors[firstWorkingDay])
                    }
                    color="secondary"
                  >
                    <FileCopyOutlinedIcon fontSize="inherit" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const useStyles = makeStyles((theme: any) => ({
  root: {
    minHeight: 350,
    display: 'flex',
    flexDirection: 'column',
  },
  toggles: {
    marginBottom: theme.spacing(2),
  },
  checkbox: {
    marginTop: theme.spacing(1),
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(),
  },
  dayRowContent: {
    display: 'flex',
    marginBottom: theme.spacing(),
    alignItems: 'center',
  },
  hoursAndLocationAndErrors: {
    display: 'flex',
    flexDirection: 'column',
  },
  hoursAndLocation: {
    display: 'flex',
    marginBottom: theme.spacing(),
    alignItems: 'center',
  },
  workHourSelectors: {
    marginTop: theme.spacing(1),
    display: 'flex',
    alignItems: 'flex-start',
  },
  copyButton: {
    marginLeft: theme.spacing(),
    marginTop: theme.spacing(0.75),
  },
  marginLeft: {
    marginLeft: theme.spacing(4),
  },
  day: {
    width: 110,
  },
  errorMessage: {
    alignSelf: 'center',
  },
}));

export default WorkHoursForm;
